/**
 * PreferenciasPrivacidade — LGPD export + delete history.
 *
 * Spec docs/notificacoes/IMPLEMENTATION_SPEC_V1V2.md §3.4 #5 / §3.8 item 5
 * + linhas 1837-1882.
 *
 * - Botoes de download CSV/JSON (LGPD art. 18, I — direito de acesso).
 * - Botao "Excluir historico" (LGPD art. 18, VI — eliminacao).
 *   Confirm modal com TextInput controlado pra digitar DELETE_ALL.
 */
import {
  Stack, Group, ThemeIcon, Text, Button, TextInput,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconDownload, IconLock, IconTrash } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { notificationPreferencesApi } from '@/api/notification-preferences'
import { formatApiError } from '@/api/errors'
import { Callout, WidgetCard } from '@/components/ui'

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function ConfirmDeleteBody({
  onTextChange,
}: { onTextChange: (s: string) => void }) {
  const [value, setValue] = useState('')
  return (
    <Stack gap="xs">
      <Text size="sm">
        Esta ação eh permanente. Os eventos originais (chamados, audits)
        permanecem; apenas o seu histórico de notificações eh apagado.
      </Text>
      <Text size="xs" c="dimmed">
        Digite <strong>DELETE_ALL</strong> para confirmar.
      </Text>
      <TextInput
        value={value}
        onChange={(e) => {
          setValue(e.currentTarget.value)
          onTextChange(e.currentTarget.value)
        }}
        placeholder="DELETE_ALL"
        autoComplete="off"
        data-autofocus
      />
    </Stack>
  )
}

export function PreferenciasPrivacidade() {
  const exportMut = useMutation({
    mutationFn: (format: 'csv' | 'json') =>
      notificationPreferencesApi.exportHistory(format).then((blob) => ({ blob, format })),
    onSuccess: ({ blob, format }) => {
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)
      triggerBlobDownload(blob, `notifications-history-${stamp}.${format}`)
      notifications.show({
        color: 'green',
        title: 'Download iniciado',
        message: `Histórico exportado em ${format.toUpperCase()}.`,
      })
    },
    onError: (err: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: formatApiError(err, 'Falha ao exportar histórico.'),
      })
    },
  })

  const deleteMut = useMutation({
    mutationFn: () => notificationPreferencesApi.deleteHistory(),
    onSuccess: (res) => {
      notifications.show({
        color: 'green',
        title: 'Histórico excluído',
        message: `${res.deleted_count} notificacoes removidas. Metadados arquivados em audit trail (30d).`,
      })
    },
    onError: (err: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Erro',
        message: formatApiError(err, 'Falha ao excluir histórico.'),
      })
    },
  })

  const confirmDeleteAll = () => {
    let typed = ''
    modals.openConfirmModal({
      title: 'Excluir todas as notificações?',
      children: <ConfirmDeleteBody onTextChange={(s) => { typed = s }} />,
      confirmProps: { color: 'red' },
      labels: { confirm: 'Excluir histórico', cancel: 'Cancelar' },
      onConfirm: () => {
        if (typed.trim() !== 'DELETE_ALL') {
          notifications.show({
            color: 'red',
            title: 'Confirmação inválida',
            message: 'Digite DELETE_ALL exatamente para confirmar.',
          })
          return
        }
        deleteMut.mutate()
      },
    })
  }

  return (
    <WidgetCard p="md">
      <Stack gap="sm">
        <Group gap={8} align="center">
          <ThemeIcon size={32} radius="md" color="teal" variant="light">
            <IconLock size={18} />
          </ThemeIcon>
          <Text fw={600} size="sm">Privacidade &amp; LGPD</Text>
        </Group>

        <Text size="xs" c="dimmed">
          Exercite seus direitos de acesso (LGPD art. 18, I) e eliminacao (art. 18, VI).
          Os eventos originais (chamados, audits) permanecem mesmo após a exclusão
          do histórico de notificações.
        </Text>

        <Callout variant="warning">
          <Text size="xs">
            A exclusão do histórico eh <strong>permanente</strong>. Metadados ficam arquivados
            em <code>notification_audit_trail</code> por 30 dias para fins de auditoria.
          </Text>
        </Callout>

        <Group gap="sm" wrap="wrap">
          <Button
            size="xs"
            variant="default"
            leftSection={<IconDownload size={14} />}
            loading={exportMut.isPending && exportMut.variables === 'csv'}
            onClick={() => exportMut.mutate('csv')}
          >
            Baixar CSV
          </Button>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconDownload size={14} />}
            loading={exportMut.isPending && exportMut.variables === 'json'}
            onClick={() => exportMut.mutate('json')}
          >
            Baixar JSON
          </Button>
          <Button
            size="xs"
            color="red"
            variant="light"
            leftSection={<IconTrash size={14} />}
            loading={deleteMut.isPending}
            onClick={confirmDeleteAll}
          >
            Excluir histórico
          </Button>
        </Group>
      </Stack>
    </WidgetCard>
  )
}
